import React from 'react';
import { useIntl } from 'umi';
import { Button } from 'tea-component';

import { DATALAB_EDITOR } from '@dbl/core/global';

import { WizardStep } from '../../constant';
import type { IStepTestPublishProps } from '../../types';

import { useStepTestPublishFlow } from './hooks/useStepTestPublishFlow';
import PublishZone from './components/PublishZone';
import ResultRows from './components/ResultRows';
import SummaryCards from './components/SummaryCards';
import TestActionBar from './components/TestActionBar';

import './index.scss';

/**
 * Step4：测试与发布。
 * - 执行测试：先 `testYamlConfigs`，成功后再 `queryYamlConfigTest`。
 * - 发布：publish，成功后回模型列表。
 * - UI 子组件位于 `./components`；流程见 `hooks/useStepTestPublishFlow`。
 */
const StepTestPublish: React.FC<IStepTestPublishProps> = (props) => {
  const {
    gameCode,
    setCurrent,
    allPrivileges,
    yamlDraft,
    cardSummary,
    yamlWorkflow,
    onYamlWorkflowChange,
  } = props;
  const {
    testPhase,
    rows,
    testRequestLoading,
    publishLoading,
  } = yamlWorkflow;
  const { cubeNameSummary, tablesSummary, dimCount, measCount } = cardSummary;
  const { yamlText, yamlLoading, savedConfigUuid } = yamlDraft;
  const { formatMessage } = useIntl();

  const hasTestPrivilege =
    allPrivileges?.includes(DATALAB_EDITOR.TestModelBtn) ?? false;
  const hasPublishPrivilege =
    allPrivileges?.includes(DATALAB_EDITOR.PublishModelBtn) ?? false;

  const yamlReady = Boolean(yamlText?.trim()) && !yamlLoading;
  const anyLoading = testRequestLoading || publishLoading;

  const testsPassedUi =
    testPhase === 'passed' && Boolean(savedConfigUuid);
  const publishEnabled = testsPassedUi;
  const publishButtonDisabled =
    !testsPassedUi || publishLoading || !hasPublishPrivilege;

  const testButtonDisabled =
    !yamlReady || !hasTestPrivilege || anyLoading;

  const { runTests, handlePublish } = useStepTestPublishFlow({
    gameCode,
    cubeNameSummary,
    hasTestPrivilege,
    hasPublishPrivilege,
    yamlWorkflow,
    yamlDraft,
    onYamlWorkflowChange,
  });

  const handlePrev = (): void => {
    setCurrent(WizardStep.Three);
  };

  return (
    <>
      <div className="dbl-model-agent-create-step dbl-model-agent-create-step-test-publish">
        <div className="dbl-model-agent-create-step-test-publish__inner">
          <SummaryCards
            cubeNameSummary={cubeNameSummary}
            tablesSummary={tablesSummary}
            dimCount={dimCount}
            measCount={measCount}
          />
          <TestActionBar
            testPhase={testPhase}
            rows={rows}
            testRequestLoading={testRequestLoading}
            testButtonDisabled={testButtonDisabled}
            onRunTests={runTests}
          />
          <ResultRows rows={rows} />
          <PublishZone
            publishEnabled={publishEnabled}
            publishButtonDisabled={publishButtonDisabled}
            publishLoading={publishLoading}
            onPublish={() => {
              void handlePublish();
            }}
          />
        </div>
      </div>
      <div className="dbl-model-agent-create-footer">
        <Button type="weak" onClick={handlePrev}>
          {formatMessage({
            id: 'dbl_model_management_create_model_previous_step',
          })}
        </Button>
      </div>
    </>
  );
};

export default StepTestPublish;
